<script setup lang="ts">
    import { computed, ref, type Ref } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import { SyncJobStatus, type SyncJob } from '@/js/services/api/backOffice/types';
    import LoadingDots from '@/js/components/LoadingDots.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import EchoPopover from '@/js/components/EchoPopover.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import IconButton from '@/js/components/IconButton.vue';
    import SortArrows from '@/js/back-office/components/SortArrows.vue';
    import { RefreshCwIcon, ChevronDownIcon, XIcon } from '@lucide/vue';

    // The caller supplies its own query composable (e.g. useQueryGetSyncJobs or
    // useQueryGetIntegrationSyncJobs) so this table stays reusable across job sources.
    const props = defineProps<{
        heading: string;
        useJobsQuery: () => {
            data: Ref<SyncJob[] | undefined>;
            isLoading: Ref<boolean>;
            isFetching: Ref<boolean>;
            refetch: () => void;
        };
    }>();

    const {
        data: syncJobs,
        isLoading: isLoadingJobs,
        isFetching: isFetchingJobs,
        refetch: refetchJobs,
    } = props.useJobsQuery();

    defineExpose({ refetch: refetchJobs });

    const statusDisplay = computed(() => ({
        [SyncJobStatus.Waiting]: {
            label: __('back-office-job-table-status-queued'),
            color: 'text-gray-medium',
        },
        [SyncJobStatus.Running]: {
            label: __('back-office-job-table-status-running'),
            color: 'text-blue-500',
        },
        [SyncJobStatus.Success]: {
            label: __('back-office-job-table-status-succeeded'),
            color: 'text-success',
        },
        [SyncJobStatus.Failed]: {
            label: __('back-office-job-table-status-failed'),
            color: 'text-error',
        },
    }));

    function formatDate(date: string | null): string {
        return date ? new Date(date).toLocaleString() : '-';
    }

    type SortColumn =
        | 'status'
        | 'id'
        | 'customerName'
        | 'systemName'
        | 'dateRange'
        | 'startedAt'
        | 'endedAt';
    const sortColumn = ref<SortColumn | null>(null);
    const sortDirection = ref<'asc' | 'desc'>('asc');
    const filterStatuses = ref<number[]>([]);
    const filterId = ref<string>('');
    const filterCustomers = ref<string[]>([]);
    const filterSystems = ref<string[]>([]);

    function focusOnMount(el: Element | object | null) {
        if (el instanceof HTMLInputElement) setTimeout(() => el.focus(), 0);
    }

    function setSort(col: SortColumn, dir: 'asc' | 'desc') {
        sortColumn.value = col;
        sortDirection.value = dir;
    }

    const sortKeyGetters: Record<SortColumn, (j: SyncJob) => string | number | null> = {
        status: (j) => j.status,
        id: (j) => j.id,
        customerName: (j) => j.customerName,
        systemName: (j) => j.systemName,
        dateRange: (j) => j.rangeStart,
        startedAt: (j) => j.startedAt,
        endedAt: (j) => j.endedAt,
    };

    const sortColumnLabels = computed<Record<SortColumn, string>>(() => ({
        status: __('back-office-job-table-col-status'),
        id: __('back-office-sync-jobs-col-id'),
        customerName: __('back-office-sync-jobs-col-customer'),
        systemName: __('back-office-sync-jobs-col-system'),
        dateRange: __('back-office-sync-jobs-col-date-range'),
        startedAt: __('back-office-job-table-col-started'),
        endedAt: __('back-office-job-table-col-ended'),
    }));

    const sortColumnLabel = computed(() =>
        sortColumn.value ? sortColumnLabels.value[sortColumn.value] : ''
    );

    const statusOptions = computed(() =>
        [SyncJobStatus.Running, SyncJobStatus.Success, SyncJobStatus.Failed].map((s) => ({
            value: s,
            label: statusDisplay.value[s].label,
        }))
    );
    const customerOptions = computed(() =>
        [...new Set((syncJobs.value ?? []).map((j) => j.customerName))].sort()
    );
    const systemOptions = computed(() =>
        [...new Set((syncJobs.value ?? []).map((j) => j.systemName))].sort()
    );

    // Some job sources leave data types empty; hide the column when no listed job has any.
    const showDataTypes = computed(() => (syncJobs.value ?? []).some((j) => j.dataTypes));
    const columnCount = computed(() => (showDataTypes.value ? 8 : 7));

    const sortedJobs = computed(() => {
        let jobs = [...(syncJobs.value ?? [])];
        if (filterStatuses.value.length) {
            jobs = jobs.filter((j) => filterStatuses.value.includes(j.status));
        }
        if (filterId.value.trim()) {
            const q = filterId.value.toLowerCase();
            jobs = jobs.filter((j) => String(j.id).toLowerCase().includes(q));
        }
        if (filterCustomers.value.length) {
            jobs = jobs.filter((j) => filterCustomers.value.includes(j.customerName));
        }
        if (filterSystems.value.length) {
            jobs = jobs.filter((j) => filterSystems.value.includes(j.systemName));
        }
        if (!sortColumn.value) return jobs;
        const getter = sortKeyGetters[sortColumn.value];
        return jobs.sort((a, b) => {
            const valA = getter(a);
            const valB = getter(b);
            if (valA === null && valB === null) return 0;
            if (valA === null) return 1;
            if (valB === null) return -1;
            const result = valA < valB ? -1 : valA > valB ? 1 : 0;
            return sortDirection.value === 'asc' ? result : -result;
        });
    });
</script>
<template>
    <div>
        <div class="flex items-center gap-2 mb-2">
            <h2 class="heading-md">{{ heading }}</h2>
            <EchoTooltip :label="__('back-office-job-table-refresh')">
                <RefreshCwIcon
                    :class="[
                        'w-5 h-5 cursor-pointer hover:text-gray-medium transition-colors',
                        { 'animate-spin': isFetchingJobs },
                    ]"
                    @click="refetchJobs()"
                />
            </EchoTooltip>
        </div>
        <div class="flex items-center gap-5 min-h-8 mb-1">
            <p class="text-caption text-gray-medium shrink-0">
                {{ __('back-office-job-table-count', { count: String(syncJobs?.length ?? 0) }) }}
            </p>
            <div class="flex flex-wrap gap-2">
                <EchoButton
                    v-if="sortColumn !== null"
                    size="small"
                    variant="chip"
                    @click="sortColumn = null"
                >
                    {{ sortColumnLabel }} {{ sortDirection === 'asc' ? '↑' : '↓' }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
                <EchoButton
                    v-for="s in filterStatuses"
                    :key="s"
                    size="small"
                    variant="chip"
                    @click="filterStatuses = filterStatuses.filter((v) => v !== s)"
                >
                    {{ statusOptions.find((o) => o.value === s)?.label }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
                <EchoButton
                    v-if="filterId.trim()"
                    size="small"
                    variant="chip"
                    @click="filterId = ''"
                >
                    {{ __('back-office-sync-jobs-col-id') }}: {{ filterId }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
                <EchoButton
                    v-for="name in filterCustomers"
                    :key="name"
                    size="small"
                    variant="chip"
                    @click="filterCustomers = filterCustomers.filter((v) => v !== name)"
                >
                    {{ name }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
                <EchoButton
                    v-for="name in filterSystems"
                    :key="name"
                    size="small"
                    variant="chip"
                    @click="filterSystems = filterSystems.filter((v) => v !== name)"
                >
                    {{ name }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
            </div>
        </div>
        <hr class="border-gray-lightest mb-1" />
        <div class="overflow-x-auto">
            <table class="w-full border-collapse">
                <thead>
                    <tr class="border-b border-gray-lightest">
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                <EchoPopover position="bottom-start">
                                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                                        <button
                                            :ref="anchor"
                                            v-bind="buttonAttrs"
                                            class="flex items-center gap-1 uppercase"
                                            @click="togglePopover"
                                        >
                                            {{ __('back-office-job-table-col-status') }}
                                            <ChevronDownIcon
                                                :class="[
                                                    'size-3',
                                                    { 'text-primary-plum': filterStatuses.length },
                                                ]"
                                            />
                                        </button>
                                    </template>
                                    <template #popover>
                                        <div class="min-w-36 py-2">
                                            <label
                                                v-for="opt in statusOptions"
                                                :key="opt.value"
                                                class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-lightest"
                                            >
                                                <input
                                                    v-model="filterStatuses"
                                                    type="checkbox"
                                                    :value="opt.value"
                                                    class="Custom-checkbox"
                                                />
                                                {{ opt.label }}
                                            </label>
                                        </div>
                                    </template>
                                </EchoPopover>
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'status' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'status' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('status', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                <EchoPopover position="bottom-start">
                                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                                        <button
                                            :ref="anchor"
                                            v-bind="buttonAttrs"
                                            class="flex items-center gap-1 uppercase"
                                            @click="togglePopover"
                                        >
                                            {{ __('back-office-sync-jobs-col-id') }}
                                            <ChevronDownIcon
                                                :class="[
                                                    'size-3',
                                                    { 'text-primary-plum': filterId.trim() },
                                                ]"
                                            />
                                        </button>
                                    </template>
                                    <template #popover>
                                        <div class="p-3">
                                            <div class="flex items-center gap-1">
                                                <input
                                                    :ref="focusOnMount"
                                                    v-model="filterId"
                                                    type="text"
                                                    :placeholder="
                                                        __(
                                                            'back-office-sync-jobs-search-placeholder'
                                                        )
                                                    "
                                                    class="border border-gray-lightest rounded px-2 py-1 text-body w-28"
                                                />
                                                <IconButton
                                                    v-if="filterId"
                                                    area-size="size-6"
                                                    size="size-4"
                                                    @click="filterId = ''"
                                                >
                                                    <XIcon class="size-4" />
                                                </IconButton>
                                            </div>
                                        </div>
                                    </template>
                                </EchoPopover>
                                <SortArrows
                                    :asc-disabled="sortColumn === 'id' && sortDirection === 'asc'"
                                    :desc-disabled="sortColumn === 'id' && sortDirection === 'desc'"
                                    @sort="(dir) => setSort('id', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                <EchoPopover position="bottom-start">
                                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                                        <button
                                            :ref="anchor"
                                            v-bind="buttonAttrs"
                                            class="flex items-center gap-1 uppercase"
                                            @click="togglePopover"
                                        >
                                            {{ __('back-office-sync-jobs-col-customer') }}
                                            <ChevronDownIcon
                                                :class="[
                                                    'size-3',
                                                    {
                                                        'text-primary-plum': filterCustomers.length,
                                                    },
                                                ]"
                                            />
                                        </button>
                                    </template>
                                    <template #popover>
                                        <div class="min-w-36 max-h-64 overflow-y-auto py-2">
                                            <label
                                                v-for="name in customerOptions"
                                                :key="name"
                                                class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-lightest"
                                            >
                                                <input
                                                    v-model="filterCustomers"
                                                    type="checkbox"
                                                    :value="name"
                                                    class="Custom-checkbox"
                                                />
                                                {{ name }}
                                            </label>
                                        </div>
                                    </template>
                                </EchoPopover>
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'customerName' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'customerName' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('customerName', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                <EchoPopover position="bottom-start">
                                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                                        <button
                                            :ref="anchor"
                                            v-bind="buttonAttrs"
                                            class="flex items-center gap-1 uppercase"
                                            @click="togglePopover"
                                        >
                                            {{ __('back-office-sync-jobs-col-system') }}
                                            <ChevronDownIcon
                                                :class="[
                                                    'size-3',
                                                    { 'text-primary-plum': filterSystems.length },
                                                ]"
                                            />
                                        </button>
                                    </template>
                                    <template #popover>
                                        <div class="min-w-36 max-h-64 overflow-y-auto py-2">
                                            <label
                                                v-for="name in systemOptions"
                                                :key="name"
                                                class="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-gray-lightest"
                                            >
                                                <input
                                                    v-model="filterSystems"
                                                    type="checkbox"
                                                    :value="name"
                                                    class="Custom-checkbox"
                                                />
                                                {{ name }}
                                            </label>
                                        </div>
                                    </template>
                                </EchoPopover>
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'systemName' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'systemName' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('systemName', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                {{ __('back-office-sync-jobs-col-date-range') }}
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'dateRange' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'dateRange' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('dateRange', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                {{ __('back-office-job-table-col-started') }}
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'startedAt' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'startedAt' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('startedAt', dir)"
                                />
                            </span>
                        </th>
                        <th class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase">
                            <span class="flex items-center gap-1">
                                {{ __('back-office-job-table-col-ended') }}
                                <SortArrows
                                    :asc-disabled="
                                        sortColumn === 'endedAt' && sortDirection === 'asc'
                                    "
                                    :desc-disabled="
                                        sortColumn === 'endedAt' && sortDirection === 'desc'
                                    "
                                    @sort="(dir) => setSort('endedAt', dir)"
                                />
                            </span>
                        </th>
                        <th
                            v-if="showDataTypes"
                            class="py-3 pl-3 pr-6 text-left text-caption-bold uppercase"
                        >
                            {{ __('back-office-sync-jobs-col-data-types') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isLoadingJobs">
                        <td :colspan="columnCount" class="py-12 text-center"><LoadingDots /></td>
                    </tr>
                    <template v-else>
                        <tr v-if="!sortedJobs.length">
                            <td
                                :colspan="columnCount"
                                class="py-6 text-body text-gray-medium text-center"
                            >
                                {{ __('back-office-job-table-empty') }}
                            </td>
                        </tr>
                        <tr
                            v-for="job in sortedJobs"
                            :key="job.id"
                            class="border-b border-gray-lightest"
                        >
                            <td
                                class="py-3 pr-6 text-left text-body"
                                :class="statusDisplay[job.status].color"
                            >
                                <EchoTooltip
                                    v-if="job.status === SyncJobStatus.Failed && job.output"
                                    :label="job.output.split(/\r?\n/)[0]"
                                >
                                    <span class="underline decoration-dotted">
                                        {{ statusDisplay[job.status].label }}
                                    </span>
                                </EchoTooltip>
                                <template v-else>{{ statusDisplay[job.status].label }}</template>
                            </td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.id }}</td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.customerName }}</td>
                            <td class="py-3 pr-6 text-left text-body">{{ job.systemName }}</td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ job.rangeStart.slice(0, 10) }} –
                                {{ job.rangeEnd.slice(0, 10) }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.startedAt) }}
                            </td>
                            <td class="py-3 pr-6 text-left text-body">
                                {{ formatDate(job.endedAt) }}
                            </td>
                            <td v-if="showDataTypes" class="py-3 pr-6 text-left text-body">
                                {{ job.dataTypes }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>
